import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ServerClock — live pill in the top bar showing current server time
 * in Europe/Moscow (GMT+3, no DST).
 *
 * The clock is driven by a single time offset derived from
 *   offsetMs = serverEpochMs - Date.now()
 * at load / re-sync. After that the tick is local (1 Hz) — no
 * per-second network. Every 5 minutes and on window focus/online we
 * re-fetch the server epoch to correct any drift, laptop-sleep
 * skew, or client-clock nudge.
 *
 * See /tmp/TZ2/ТЗ-серверное-время.md for the full spec.
 */
export default function ServerClock({ compact = false, testid = 'server-clock' }) {
  const [now, setNow] = useState(() => new Date());
  const [ready, setReady] = useState(false);
  const offsetRef = useRef(0); // serverEpochMs - Date.now()

  const lastSyncRef = useRef(0);
  const syncFromServer = React.useCallback(async () => {
    try {
      const t0 = Date.now();
      lastSyncRef.current = t0;
      const r = await fetch(`${API_URL}/api/server-time`, { cache: 'no-store' });
      if (!r.ok) return;
      const d = await r.json();
      const t1 = Date.now();
      // Compensate for one-way network delay: assume symmetric latency.
      const halfLatency = Math.max(0, Math.floor((t1 - t0) / 2));
      const serverAtT1 = (d.epochMs || 0) + halfLatency;
      offsetRef.current = serverAtT1 - t1;
      setReady(true);
    } catch (_) { /* silent — keep last known offset */ }
  }, []);

  useEffect(() => {
    syncFromServer();
    // Local 1 Hz tick — moves off the last known serverEpochMs offset.
    const tickId = setInterval(() => setNow(new Date()), 1000);
    // Periodic re-sync every 5 minutes to eliminate drift.
    const syncId = setInterval(syncFromServer, 5 * 60 * 1000);
    // Re-sync on focus / visibilitychange / online — laptop wake, tab
    // return, connectivity flip all leave the local `Date.now` out of
    // step with the server. Throttled to once a minute so alt-tabbing
    // cannot turn into a stream of /api/server-time calls.
    const onWake = () => {
      if (document.hidden) return;
      if (Date.now() - lastSyncRef.current < 60000) return;
      syncFromServer();
    };
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      clearInterval(tickId);
      clearInterval(syncId);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [syncFromServer]);

  // Convert current local wall-clock to server UTC, then shift by +3.
  // No JS DateTimeFormat call per tick — much cheaper than tz-aware
  // formatters and avoids browsers that don't have Moscow data.
  const serverUtcMs = now.getTime() + offsetRef.current;
  const gmt3 = new Date(serverUtcMs + 3 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  const hhmmss = ready
    ? `${pad(gmt3.getUTCHours())}:${pad(gmt3.getUTCMinutes())}:${pad(gmt3.getUTCSeconds())}`
    : '--:--:--';

  return (
    <div
      className="hidden md:inline-flex items-center flex-shrink-0"
      style={{
        gap: 11,
        padding: '7px 8px 7px 14px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.28)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
      }}
      data-testid={testid}
      title={`Server time in Europe/Moscow (GMT+3). Auto-syncs every 5 min.`}
    >
      <Clock className="w-4 h-4" style={{ color: '#bcd6f2' }} aria-hidden />
      <div className="flex flex-col leading-tight">
        {!compact && (
          <span
            className={compact ? 'hidden' : 'hidden xl:inline'}
            style={{
              fontSize: 8.5,
              fontWeight: 700,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              color: '#bcd6f2',
              lineHeight: 1,
              marginBottom: 2,
            }}
          >
            Server Time
          </span>
        )}
        <span
          style={{
            fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 16,
            fontWeight: 700,
            color: '#ffffff',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            minWidth: '8ch',
          }}
          data-testid={`${testid}-value`}
          data-value={hhmmss}
        >
          {hhmmss}
        </span>
      </div>
      <span
        style={{
          background: 'rgba(255,255,255,0.9)',
          color: '#1160b0',
          fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 10,
          fontWeight: 700,
          borderRadius: 999,
          padding: '4px 7px',
        }}
      >
        GMT+3
      </span>
    </div>
  );
}
